/* eslint-disable camelcase */
import type { Region } from '@/modules/region/domain/Region';
import { LABELS } from '@/sections/address/components/AddressForm/Autocomplete/constants';
import type {
  IPlaceResult,
  PlacesAutocompleteResult
} from '@/sections/address/components/AddressForm/Autocomplete/interfaces';

export const getCleanComponentForm = (): PlacesAutocompleteResult => ({
  street_number: '',
  route: '',
  postal_town: '',
  administrative_area_level_3: '',
  locality: '',
  administrative_area_level_1: '',
  administrative_area_level_2: '',
  country_short_name: '',
  postal_code: '',
  plus_code: '',
  address_formatted: '',
  country_long_name: '',
  latitude: undefined,
  longitude: undefined
});

const setNameRegion = (nameRegion: string, regions: Region[]): string => {
  const {
    EMPTY_STRING,
    LONG_LIBERATOR,
    METROPOLITAN,
    OHIGGINS,
    SHORT_LIBERATOR
  } = LABELS.REGIONS_NAME;

  const isOHiggins = nameRegion === SHORT_LIBERATOR || nameRegion === OHIGGINS;
  if (isOHiggins) return LONG_LIBERATOR;
  if (nameRegion === METROPOLITAN) return nameRegion;

  const regionName = regions?.find((region) => {
    if (region?.name?.toLowerCase()?.includes(nameRegion?.toLowerCase())) {
      return region;
    }
  });
  return regionName ? regionName?.name : EMPTY_STRING;
};

export const getAddressComponent = (place: IPlaceResult, regions: Region[]) => {
  if (!place) return undefined;
  if (!regions || !Array.isArray(regions)) return undefined;

  const components = place?.address_components;
  const componentForm = getCleanComponentForm();
  let regionAndComunas;
  let comunaObject;
  let countryLongName = '';

  if (!components) return undefined;

  for (let i = 0; i < components?.length; i++) {
    const component = components?.[i];
    switch (component?.types?.[0]) {
      case 'street_number':
        componentForm.street_number = component?.short_name;
        break;
      case 'route':
        componentForm.route = component?.long_name?.indexOf(
          component?.short_name
        )
          ? component?.short_name
          : component?.long_name;
        break;
      case 'postal_town':
        componentForm.postal_town = component?.long_name;
        break;
      case 'administrative_area_level_3':
        componentForm.administrative_area_level_3 = component?.long_name;
        break;
      case 'locality':
        componentForm.locality = component?.long_name;
        break;
      case 'administrative_area_level_1':
        componentForm.administrative_area_level_1 = component?.short_name;
        componentForm.stateNameCustom = setNameRegion(
          component?.short_name,
          regions
        );
        regionAndComunas = regions?.find(
          (region) => region?.name === componentForm?.stateNameCustom
        );
        break;
      case 'administrative_area_level_2':
        componentForm.administrative_area_level_2 = component?.short_name;
        break;
      case 'country':
        componentForm.country_short_name = component?.short_name;
        countryLongName = component?.long_name;
        break;
      case 'postal_code':
        componentForm.postal_code = component?.short_name;
    }
  }

  const latlong = {
    latitude: place?.geometry?.location?.lat() ?? undefined,
    longitude: place?.geometry?.location?.lng() ?? undefined
  };

  const globalCode = place?.plus_code?.global_code ?? '';
  const hasPlusCode = Object.prototype.hasOwnProperty.call(place, 'plus_code');
  componentForm.plus_code = hasPlusCode ? globalCode : '';
  componentForm.address_formatted = place?.formatted_address ?? '';

  if (regionAndComunas) {
    componentForm.region_id = regionAndComunas?.id?.toString();
    const localityNew = regionAndComunas?.communes?.find(
      (commune) =>
        normalize(commune?.name?.replace(/ /g, '')) ===
        normalize(componentForm?.locality?.toUpperCase().replace(/ /g, ''))
    );
    const localityNew2 = regionAndComunas?.communes?.find(
      (comuna) =>
        normalize(comuna?.name?.replace(/ /g, '')) ===
        normalize(
          componentForm?.administrative_area_level_3
            ?.toUpperCase()
            ?.replace(/ /g, '')
        )
    );

    if (localityNew) {
      componentForm.locality = localityNew?.name;
    } else if (localityNew2) {
      componentForm.locality = localityNew2?.name;
    } else {
      componentForm.locality = componentForm?.locality ?? '';
    }

    comunaObject = componentForm?.region_id
      ? regionAndComunas?.communes?.find(
          (comuna) =>
            normalize(comuna?.name?.replace(/ /g, '')) ===
            normalize(componentForm?.locality?.toUpperCase()?.replace(/ /g, ''))
        )
      : {};
    componentForm.comuna_id = comunaObject ? String(comunaObject?.id) : '';
  }

  const addressComplete: PlacesAutocompleteResult = {
    ...componentForm,
    ...latlong
  };

  return {
    addressComplete,
    isValid: validateAddressAutoComplete(addressComplete),
    countryLongName,
    latlong
  };
};

const normalize = (str: string): string => {
  if (!str) return '';

  const from: string = 'ÃÀÁÄÂÈÉËÊÌÍÏÎÒÓÖÔÙÚÜÛãàáäâèéëêìíïîòóöôùúüûÑñÇç';
  const to: string = 'AAAAAEEEEIIIIOOOOUUUUaaaaaeeeeiiiioooouuuunncc';
  const mapping: { [key: string]: string } = {};
  for (let i = 0, j = from.length; i < j; i++) {
    mapping[from.charAt(i)] = to.charAt(i);
  }

  const ret: string[] = [];
  for (let i: number = 0, j: number = str.length; i < j; i++) {
    const c: string = str.charAt(i);
    if (Object.prototype.hasOwnProperty.call(mapping, str.charAt(i))) {
      ret.push(mapping[c]);
    } else {
      ret.push(c);
    }
  }
  return ret.join('');
};

const validateAddressAutoComplete = (
  addressComplete: PlacesAutocompleteResult
): boolean => {
  if (
    addressComplete.street_number === '' ||
    addressComplete.route === '' ||
    addressComplete.locality === '' ||
    addressComplete.region_id === '' ||
    addressComplete.comuna_id === ''
  ) {
    return false;
  } else {
    return true;
  }
};
